import * as stylex from '@stylexjs/stylex'
import { useEffect, useId, useRef } from 'react'

import Icon from './Icon'


const styles = stylex.create({
  s0: {
    position: "fixed",
    insetInline: "0px",
    top: "0px",
    height: "100dvh",
    zIndex: 100,
    display: "flex",
    alignItems: "end",
    justifyContent: "center",
    backgroundColor: "color-mix(in srgb, #172018 45%, transparent)",
    padding: "0px",
    backdropFilter: "blur(1px)",
    "@media (min-width: 640px)": {
      alignItems: "center",
      padding: "20px",
    },
  },
  s1: {
    display: "flex",
    maxHeight: "100%",
    width: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: "24px",
    borderTopRightRadius: "24px",
    backgroundColor: "#f3f2eb",
    boxShadow: "0 24px 60px rgba(27,38,29,.28)",
    "@media (min-width: 640px)": {
      borderRadius: "22px",
    },
  },
  medium: { maxWidth: "640px" },
  large: { maxWidth: "760px" },
  fullscreen: { height: '100%', maxWidth: '100%', borderRadius: 0, borderTopLeftRadius: 0, borderTopRightRadius: 0 },
  fullscreenOverlay: { padding: 0 },
  fullscreenBody: { flex: '1 1 0%' },
  headingContent: { minWidth: 0, flex: '1 1 0%' },
  s2: {
    display: "flex",
    flexShrink: "0",
    alignItems: "start",
    justifyContent: "space-between",
    gap: "16px",
    borderBottomWidth: 1,
    borderColor: "#c6d2c1",
    backgroundColor: "#e7eee2",
    paddingInline: "20px",
    paddingBlock: "16px",
    backdropFilter: "blur(8px)",
    "@media (min-width: 640px)": {
      paddingInline: "28px",
    },
  },
  s3: {
    overflowWrap: 'anywhere',
    margin: 0,
    fontSize: "20px",
    fontWeight: 600,
    letterSpacing: "-.02em",
    color: "#212622",
  },
  s4: {
    marginTop: "4px",
    marginBottom: 0,
    fontSize: "13px",
    lineHeight: "1.25rem",
    color: "#536158",
  },
  s5: {
    display: "flex",
    height: "44px",
    width: "44px",
    flexShrink: "0",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "9999px",
    color: "#66706a",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#eef2ec",
    },
    ":focus-visible": {
      outlineStyle: "solid",
      outlineWidth: 2,
      outlineOffset: 2,
      outlineColor: "#3f8654",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.4,
    },
  },
  s6: {
    minHeight: "0px",
    overflowY: "auto",
    overscrollBehavior: "contain",
    paddingInline: "20px",
    paddingBottom: "max(24px, env(safe-area-inset-bottom))",
    paddingTop: "20px",
    "@media (min-width: 640px)": {
      paddingInline: "28px",
      paddingBottom: "28px",
    },
  },
  footer: {
    flexShrink: 0,
    borderTopWidth: 1,
    borderColor: "#c6d2c1",
    backgroundColor: "#fffefa",
    paddingInline: "20px",
    paddingTop: "12px",
    paddingBottom: "max(12px, env(safe-area-inset-bottom))",
    "@media (min-width: 640px)": { paddingInline: "28px" },
  },
})
type DialogProps = {
	children: React.ReactNode
	closeDisabled?: boolean
	description?: string
  footer?: React.ReactNode
  fullscreen?: boolean
  onClose: () => void
  size?: 'md' | 'lg'
  title: string
}

export default function Dialog({ children, closeDisabled = false, description, footer, fullscreen = false, onClose, size = 'md', title }: DialogProps) {
  const titleID = useId()
  const descriptionID = useId()
  const closeRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const closeState = useRef({ closeDisabled, onClose })

  useEffect(() => {
    closeState.current = { closeDisabled, onClose }
  }, [closeDisabled, onClose])

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const initialFocus = window.matchMedia('(pointer: fine)').matches
      ? dialogRef.current?.querySelector<HTMLElement>('[data-dialog-autofocus]')
      : null
    if (initialFocus) {
      initialFocus.focus()
    } else {
      closeRef.current?.focus()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !closeState.current.closeDisabled) {
        closeState.current.onClose()
      }
      if (event.key !== 'Tab' || !dialogRef.current) {
        return
      }
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled)'))
        .filter((element) => element.getClientRects().length > 0)
      if (focusable.length === 0) {
        event.preventDefault()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!dialogRef.current.contains(document.activeElement)) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const viewport = window.visualViewport
    const fitViewport = () => {
      if (viewport && overlayRef.current) {
        overlayRef.current.style.height = `${viewport.height}px`
        overlayRef.current.style.top = `${viewport.offsetTop}px`
      }
    }
    fitViewport()
    viewport?.addEventListener('resize', fitViewport)
    viewport?.addEventListener('scroll', fitViewport)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      viewport?.removeEventListener('resize', fitViewport)
      viewport?.removeEventListener('scroll', fitViewport)
      previousFocus?.focus({ preventScroll: true })
    }
  }, [])

  const handleClose = closeDisabled ? () => undefined : onClose
  return (
    <div {...stylex.props(styles.s0, fullscreen && styles.fullscreenOverlay)} onMouseDown={handleClose} ref={overlayRef}>
      <section aria-describedby={description ? descriptionID : undefined} aria-labelledby={titleID} aria-modal="true" {...stylex.props(styles.s1, size === 'lg' ? styles.large : styles.medium, fullscreen && styles.fullscreen)} onMouseDown={(event) => event.stopPropagation()} ref={dialogRef} role="dialog">
        <header {...stylex.props(styles.s2)}>
          <div {...stylex.props(styles.headingContent)}>
            <h2 {...stylex.props(styles.s3)} id={titleID}>{title}</h2>
            {description && <p {...stylex.props(styles.s4)} id={descriptionID}>{description}</p>}
          </div>
          <button aria-label="Close dialog" {...stylex.props(styles.s5)} disabled={closeDisabled} onClick={handleClose} ref={closeRef} type="button"><Icon name="close" /></button>
        </header>
        <div {...stylex.props(styles.s6, fullscreen && styles.fullscreenBody)}>{children}</div>
        {footer && <footer {...stylex.props(styles.footer)}>{footer}</footer>}
      </section>
    </div>
  )
}
